import { useState } from 'react'
import { 
  EditIcon,
  CheckIcon,
  XIcon,
  PhoneIcon,
  MailIcon,
  MapPinIcon
} from '../Icons'
import { Product } from '../../data/products'

interface ProfileScreenLabProps {
  orders: Product[]
  version: 'v1' | 'v3'
}

interface UserData {
  firstName: string
  lastName: string
  username: string
  phone: string
  email: string
  address: string
}

const colors = {
  bg: '#050508',
  cardBg: '#141418',
  nestedBg: '#1c1c22',
  accent: '#9333ea',
  accentLight: '#a855f7',
  accentBg: 'rgba(147, 51, 234, 0.12)',
  accentBorder: 'rgba(147, 51, 234, 0.25)',
  cardBorder: 'rgba(255, 255, 255, 0.12)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.52)',
  green: '#22c55e',
  yellow: '#fbbf24',
  blue: '#3b82f6',
  red: '#ef4444',
}

// Получаем данные пользователя из Telegram
function getTelegramUserData(): UserData {
  const tg = (window as any).Telegram?.WebApp
  const user = tg?.initDataUnsafe?.user
  
  return {
    firstName: user?.first_name || 'Имя',
    lastName: user?.last_name || 'Фамилия',
    username: user?.username || 'username',
    phone: '+7 (900) 123-45-67',
    email: 'user@example.com',
    address: 'Не указан',
  }
}

export default function ProfileScreenLab({ orders, version }: ProfileScreenLabProps) {
  if (version === 'v1') {
    return <ProfileV1 orders={orders} />
  } else {
    return <ProfileV3 orders={orders} />
  }
}

// VERSION 1: Карточный стиль с редактированием
function ProfileV1({ orders }: { orders: Product[] }) {
  const [userData, setUserData] = useState(getTelegramUserData())
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState(userData)

  const totalSpent = orders.reduce((sum, product) => {
    const priceStr = (product.price || '0').replace(/[^\d]/g, '')
    return sum + (parseInt(priceStr) || 0)
  }, 0)

  const handleSave = () => {
    setUserData(editData)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditData(userData)
    setIsEditing(false)
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      paddingBottom: '80px',
    }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
        padding: '24px 16px 32px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#fff',
            margin: 0,
          }}>
            Профиль
          </h2>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              style={{
                padding: '8px 16px',
                borderRadius: '10px',
                border: 'none',
                background: 'rgba(255, 255, 255, 0.2)',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <EditIcon size={16} />
              Редактировать
            </button>
          )}
        </div>

        {/* User Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '16px',
          padding: '20px',
          backdropFilter: 'blur(10px)',
          textAlign: 'center',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px',
            fontSize: '32px',
            fontWeight: 700,
            color: '#fff',
          }}>
            {userData.firstName[0]}{userData.lastName[0]}
          </div>
          <div style={{
            fontSize: '22px',
            fontWeight: 700,
            color: '#fff',
            marginBottom: '4px',
          }}>
            {userData.firstName} {userData.lastName}
          </div>
          <div style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.8)',
          }}>
            @{userData.username}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
        padding: '16px',
      }}>
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: '12px',
          padding: '16px 12px',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: colors.accentLight,
            marginBottom: '4px',
          }}>
            {orders.length}
          </div>
          <div style={{
            fontSize: '12px',
            color: colors.textMuted,
          }}>
            Заказов
          </div>
        </div>
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: '12px',
          padding: '16px 12px',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: colors.blue,
            marginBottom: '4px',
          }}>
            ₽{(totalSpent / 1000).toFixed(1)}k
          </div>
          <div style={{
            fontSize: '12px',
            color: colors.textMuted,
          }}>
            Потрачено
          </div>
        </div>
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: '12px',
          padding: '16px 12px',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: colors.yellow,
            marginBottom: '4px',
          }}>
            4.9
          </div>
          <div style={{
            fontSize: '12px',
            color: colors.textMuted,
          }}>
            Рейтинг
          </div>
        </div>
      </div>

      {/* User Details */}
      <div style={{ padding: '0 16px 16px' }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: 700,
          color: colors.text,
          marginBottom: '12px',
        }}>
          Личные данные
        </h3>

        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: '16px',
          overflow: 'hidden',
        }}>
          {/* Phone */}
          <div style={{
            padding: '16px',
            borderBottom: `1px solid ${colors.cardBorder}`,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <PhoneIcon size={20} color={colors.textMuted} />
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '12px',
                  color: colors.textMuted,
                  marginBottom: '4px',
                }}>
                  Телефон
                </div>
                {isEditing ? (
                  <input
                    type="tel"
                    value={editData.phone}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.cardBorder}`,
                      background: colors.nestedBg,
                      color: colors.text,
                      fontSize: '14px',
                    }}
                  />
                ) : (
                  <div style={{
                    fontSize: '15px',
                    fontWeight: 500,
                    color: colors.text,
                  }}>
                    {userData.phone}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Email */}
          <div style={{
            padding: '16px',
            borderBottom: `1px solid ${colors.cardBorder}`,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <MailIcon size={20} color={colors.textMuted} />
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '12px',
                  color: colors.textMuted,
                  marginBottom: '4px',
                }}>
                  Email
                </div>
                {isEditing ? (
                  <input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.cardBorder}`,
                      background: colors.nestedBg,
                      color: colors.text,
                      fontSize: '14px',
                    }}
                  />
                ) : (
                  <div style={{
                    fontSize: '15px',
                    fontWeight: 500,
                    color: colors.text,
                  }}>
                    {userData.email}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div style={{
            padding: '16px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <MapPinIcon size={20} color={colors.textMuted} />
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '12px',
                  color: colors.textMuted,
                  marginBottom: '4px',
                }}>
                  Адрес доставки
                </div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.address}
                    onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.cardBorder}`,
                      background: colors.nestedBg,
                      color: colors.text,
                      fontSize: '14px',
                    }}
                  />
                ) : (
                  <div style={{
                    fontSize: '15px',
                    fontWeight: 500,
                    color: colors.text,
                  }}>
                    {userData.address}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit Buttons */}
        {isEditing && (
          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '16px',
          }}>
            <button
              onClick={handleSave}
              style={{
                flex: 1,
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
                color: '#fff',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <CheckIcon size={20} />
              Сохранить
            </button>
            <button
              onClick={handleCancel}
              style={{
                flex: 1,
                padding: '14px',
                borderRadius: '12px',
                border: `1px solid ${colors.cardBorder}`,
                background: colors.cardBg,
                color: colors.text,
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <XIcon size={20} />
              Отмена
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// VERSION 3: Табличный стиль
function ProfileV3({ orders }: { orders: Product[] }) {
  const userData = getTelegramUserData()

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '16px',
      paddingBottom: '80px',
    }}>
      <h2 style={{
        fontSize: '24px',
        fontWeight: 700,
        color: colors.text,
        marginBottom: '20px',
      }}>
        Профиль пользователя
      </h2>

      {/* User Info Table */}
      <div style={{
        background: colors.cardBg,
        borderRadius: '16px',
        border: `1px solid ${colors.cardBorder}`,
        overflow: 'hidden',
        marginBottom: '16px',
      }}>
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${colors.cardBorder}`,
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '14px', color: colors.textMuted }}>Имя</span>
          <span style={{ fontSize: '14px', color: colors.text, fontWeight: 600 }}>
            {userData.firstName} {userData.lastName}
          </span>
        </div>
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${colors.cardBorder}`,
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '14px', color: colors.textMuted }}>Username</span>
          <span style={{ fontSize: '14px', color: colors.text, fontWeight: 600 }}>
            @{userData.username}
          </span>
        </div>
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${colors.cardBorder}`,
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '14px', color: colors.textMuted }}>Телефон</span>
          <span style={{ fontSize: '14px', color: colors.text, fontWeight: 600 }}>
            {userData.phone}
          </span>
        </div>
        <div style={{
          padding: '16px',
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '14px', color: colors.textMuted }}>Email</span>
          <span style={{ fontSize: '14px', color: colors.text, fontWeight: 600 }}>
            {userData.email}
          </span>
        </div>
      </div>

      {/* Stats Table */}
      <div style={{
        background: colors.cardBg,
        borderRadius: '16px',
        border: `1px solid ${colors.cardBorder}`,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${colors.cardBorder}`,
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '14px', color: colors.textMuted }}>Всего заказов</span>
          <span style={{ fontSize: '16px', color: colors.accentLight, fontWeight: 700 }}>
            {orders.length}
          </span>
        </div>
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${colors.cardBorder}`,
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '14px', color: colors.textMuted }}>Потрачено</span>
          <span style={{ fontSize: '16px', color: colors.blue, fontWeight: 700 }}>
            ₽12,000
          </span>
        </div>
        <div style={{
          padding: '16px',
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '14px', color: colors.textMuted }}>Рейтинг</span>
          <span style={{ fontSize: '16px', color: colors.yellow, fontWeight: 700 }}>
            ⭐ 4.9
          </span>
        </div>
      </div>
    </div>
  )
}
